import React, { useEffect, useRef } from "react";
import styles from "./styles/ViewOnly.module.css";

/*
  A subtree whose FIELDS cannot be edited, but which can still be read and
  navigated.

  Used where a partner is looking at something the customer wrote. Hiding the
  Save button is not enough: a field that still accepts typing invites an edit
  that will never be kept, because the writers in that view are stubbed and the
  work vanishes on close.

  Fields only, deliberately. A partner whose customer shared their evaluations
  should still be able to open the value history and the debt records -- those
  are reading. So buttons are left alone and only data entry is stopped.

  Two mechanisms, and both are needed:

  - Every input, textarea and select is given `disabled`. That is what "not
    editable" actually means -- no typing, out of the tab order, and reported
    properly to assistive technology.
  - `pointer-events: none` on the wrapper, which is inherited. Necessary because
    some widgets hang their handler on a wrapper rather than on a form control:
    MUI's Switch puts onClick on its root span, so disabling the checkbox inside
    it stops nothing at all. That is how the "is a minor" and "notify now"
    toggles stayed live after the fields were disabled.

  Neither alone is enough. pointer-events leaves the keyboard alone; `disabled`
  never sees a click that lands on a div.

  Elements marked `data-viewonly-allow` are exempt -- the value and debt history
  expanders, which only reveal what is already shared.

  `display: contents` so the wrapper adds no box: these modals are dense flex
  and grid work, and an extra div would shift them.
*/

const FIELDS = "input, textarea, select";

export default function ViewOnly({ active, children }) {
  const ref = useRef(null);

  useEffect(() => {
    const root = ref.current;
    if (!root) return;

    if (!active) {
      // Release anything this component disabled, and nothing else.
      root.querySelectorAll("[data-viewonly-disabled]").forEach((el) => {
        el.disabled = false;
        el.removeAttribute("data-viewonly-disabled");
      });
      return;
    }

    /*
      A subtree the viewer MAY change, inside one they may not.

      A partner is read-only by default, but a customer can grant them edit
      permission on individual parts of a record -- the description, the
      evaluations -- and those parts stay live while everything around them
      stays locked. The section marks itself; this only has to leave it alone,
      both here and in the stylesheet's pointer-events.
    */
    const EDITABLE = "[data-viewonly-editable]";
    const editable = (el) => el.closest(EDITABLE) !== null;

    const lock = (scope) => {
      scope.querySelectorAll(FIELDS).forEach((el) => {
        // Leave alone anything already disabled for its own reasons, so
        // releasing later cannot enable a control that was never ours.
        if (el.disabled) return;
        if (editable(el)) return;
        el.disabled = true;
        el.setAttribute("data-viewonly-disabled", "");
      });
    };

    lock(root);
    const observer = new MutationObserver((records) => {
      for (const record of records) {
        for (const node of record.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.matches?.(FIELDS)) {
            if (!node.disabled && !editable(node)) {
              node.disabled = true;
              node.setAttribute("data-viewonly-disabled", "");
            }
          }
          lock(node);
        }
      }
    });
    observer.observe(root, { childList: true, subtree: true });
    return () => observer.disconnect();
  });

  return (
    <div
      ref={ref}
      className={active ? styles.view_only : undefined}
      style={{ display: "contents" }}
    >
      {children}
    </div>
  );
}
